/**
 * „Uprawnienia w organizacji” w karcie pracownika hali (PR-7, migracja 892).
 *
 * Rola pracownika logującego się PIN-em pochodzi z JEDNEJ kartoteki ról
 * Universe — `user_organizations` — przez konto techniczne pracownika
 * (`shop_floor_workers.user_id`). Decyzja właściciela z 16.10.2026 (pytanie 7):
 * drugiej kartoteki ról dla telefonu nie ma. Brak wiersza = pracownik (stan
 * nazwany, nie usterka).
 *
 * Zapis idzie wyłącznie przez RPC `universe_worker_rola_ustaw`, które
 * rozstrzyga uprawnienia w bazie (administrator organizacji albo super-admin,
 * tylko konto techniczne, bez owner/super_admin/partner) — ten ekran jest
 * wygodą, nie bramką. Pole widzi tylko administrator.
 *
 * Trzy stany odczytu: wczytuję / nie udało się / wartość. „Nie udało się” nie
 * udaje „Pracownik”, bo administrator nadałby wtedy rolę, którą już widzi
 * jako brak.
 */
import { Loader2, ShieldCheck } from 'lucide-react';
import { useEffect, useState } from 'react';

import { supabase } from '../../../lib/supabase';

/** Role, które karta pracownika nadaje (pozostałe dopuszczalne RPC zostają widoczne, gdy już są). */
export const ROLE_KARTY: readonly { value: string; label: string; opis: string }[] = [
  {
    value: '',
    label: 'Pracownik',
    opis: 'Bez uprawnień w organizacji — korzysta z przydzielonych organów.',
  },
  {
    value: 'manager',
    label: 'Kierownik',
    opis: 'Może zmieniać dane organizacji; w telefonie paruje ekrany i odpina urządzenia.',
  },
  {
    value: 'admin',
    label: 'Administrator',
    opis: 'Jak kierownik, a do tego zarządza ludźmi i ustawieniami organizacji.',
  },
];

const NAZWY_INNYCH_ROL: Record<string, string> = {
  supervisor: 'Brygadzista',
  employee: 'Pracownik z kontem',
  trainee: 'Praktykant',
  owner: 'Właściciel',
  super_admin: 'Operator platformy',
  partner: 'Partner',
};

/** Role, których karta nie zmienia — pokazuje je tylko do odczytu. */
const ROLE_POZA_KARTA = new Set(['owner', 'super_admin', 'partner']);

type Odczyt = { stan: 'wczytuje' } | { stan: 'blad' } | { stan: 'jest'; rola: string };

/** Zdanie dla administratora z kodu odmowy RPC (892). */
export function opisOdmowyRoli(blad: { code?: string; message?: string } | null): string {
  const kod = blad?.code ?? '';
  const tresc = blad?.message ?? '';
  if (tresc.includes('kontem uzytkownika')) {
    return 'Ten pracownik loguje się kontem użytkownika — jego rolę zmienia się na liście Użytkownicy.';
  }
  if (tresc.includes('aktywnemu pracownikowi')) {
    return 'Rola przysługuje tylko aktywnemu pracownikowi. Włącz go, a potem nadaj rolę.';
  }
  if (kod === '42501') return 'Brak uprawnień — rolę nadaje administrator organizacji.';
  if (kod === '22023') return 'Tej roli nie nadaje się z karty pracownika.';
  return 'Nie udało się zapisać roli. Spróbuj ponownie.';
}

interface Props {
  workerId: string;
  /** Konto techniczne pracownika; bez niego nie ma czego powiązać. */
  userId: string | null;
  organizationId: string;
  workerActive: boolean;
}

export function RolaWOrganizacji({ workerId, userId, organizationId, workerActive }: Props) {
  const [odczyt, setOdczyt] = useState<Odczyt>({ stan: 'wczytuje' });
  const [wybor, setWybor] = useState('');
  const [zapisuje, setZapisuje] = useState(false);
  const [komunikat, setKomunikat] = useState<{ ton: 'ok' | 'blad'; tekst: string } | null>(null);

  useEffect(() => {
    let aktualny = true;
    if (!userId) {
      setOdczyt({ stan: 'jest', rola: '' });
      return () => {
        aktualny = false;
      };
    }
    setOdczyt({ stan: 'wczytuje' });
    void supabase
      .from('user_organizations')
      .select('role')
      .eq('organization_id', organizationId)
      .eq('user_id', userId)
      .maybeSingle()
      .then(({ data, error }) => {
        if (!aktualny) return;
        if (error) {
          setOdczyt({ stan: 'blad' });
          return;
        }
        const rola = (data as { role?: string } | null)?.role ?? '';
        setOdczyt({ stan: 'jest', rola });
        setWybor(rola);
      });
    return () => {
      aktualny = false;
    };
  }, [organizationId, userId]);

  if (!userId) return null;

  const naglowek = (
    <div>
      <h4 className="text-sm font-semibold text-aurora-text flex items-center gap-2">
        <ShieldCheck className="w-4 h-4 text-aurora-brand" />
        Uprawnienia w organizacji
      </h4>
      <p className="text-xs text-aurora-subtle mt-1">
        Decydują o tym, co pracownik może w telefonie poza swoją pracą: parowanie ekranów i
        odpinanie urządzeń.
      </p>
    </div>
  );

  if (odczyt.stan === 'wczytuje') {
    return (
      <section className="pt-4 border-t border-aurora-border space-y-3">
        {naglowek}
        <p className="text-sm text-aurora-muted flex items-center gap-2" role="status">
          <Loader2 className="w-4 h-4 animate-spin" />
          Wczytuję uprawnienia…
        </p>
      </section>
    );
  }

  if (odczyt.stan === 'blad') {
    return (
      <section className="pt-4 border-t border-aurora-border space-y-3">
        {naglowek}
        <p className="text-sm text-aurora-danger" role="alert">
          Nie udało się wczytać uprawnień pracownika. Zamknij kartę i otwórz ją ponownie.
        </p>
      </section>
    );
  }

  const obecna = odczyt.rola;

  if (ROLE_POZA_KARTA.has(obecna)) {
    return (
      <section className="pt-4 border-t border-aurora-border space-y-3">
        {naglowek}
        <p className="text-sm text-aurora-muted">
          {NAZWY_INNYCH_ROL[obecna] ?? obecna} — tej roli nie zmienia się z karty pracownika.
        </p>
      </section>
    );
  }

  const opcje = ROLE_KARTY.some((r) => r.value === obecna)
    ? ROLE_KARTY
    : [
        ...ROLE_KARTY,
        { value: obecna, label: NAZWY_INNYCH_ROL[obecna] ?? obecna, opis: 'Nadana wcześniej.' },
      ];
  const opisWyboru = opcje.find((o) => o.value === wybor)?.opis;
  const zmieniona = wybor !== obecna;
  const nadanieNieaktywnemu = !workerActive && wybor !== '';

  const zapisz = async () => {
    setZapisuje(true);
    setKomunikat(null);
    const { data, error } = await supabase.rpc('universe_worker_rola_ustaw', {
      p_worker_id: workerId,
      p_rola: wybor === '' ? null : wybor,
    });
    setZapisuje(false);
    if (error) {
      setKomunikat({ ton: 'blad', tekst: opisOdmowyRoli(error) });
      setWybor(obecna);
      return;
    }
    const nowa = (data as string | null) ?? '';
    setOdczyt({ stan: 'jest', rola: nowa });
    setWybor(nowa);
    setKomunikat({ ton: 'ok', tekst: 'Zapisano uprawnienia.' });
  };

  return (
    <section className="pt-4 border-t border-aurora-border space-y-3">
      {naglowek}
      <div>
        <label htmlFor={`rola-org-${workerId}`} className="field__label block mb-1.5">
          Rola w organizacji
        </label>
        <div className="flex flex-wrap items-center gap-2">
          <select
            id={`rola-org-${workerId}`}
            className="select"
            value={wybor}
            disabled={zapisuje}
            onChange={(e) => {
              setWybor(e.target.value);
              setKomunikat(null);
            }}
          >
            {opcje.map((o) => (
              <option key={o.value || 'brak'} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn--secondary btn--sm"
            disabled={!zmieniona || zapisuje || nadanieNieaktywnemu}
            onClick={() => void zapisz()}
          >
            {zapisuje ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Zapisz uprawnienia
          </button>
        </div>
        {opisWyboru ? <p className="text-xs text-aurora-subtle mt-1">{opisWyboru}</p> : null}
        {nadanieNieaktywnemu ? (
          <p className="text-xs text-aurora-warning mt-1">
            Rola przysługuje tylko aktywnemu pracownikowi.
          </p>
        ) : null}
        {komunikat ? (
          <p
            className={`text-sm mt-2 ${komunikat.ton === 'ok' ? 'text-aurora-success' : 'text-aurora-danger'}`}
            role={komunikat.ton === 'ok' ? 'status' : 'alert'}
          >
            {komunikat.tekst}
          </p>
        ) : null}
      </div>
    </section>
  );
}
